/**
 * The inspector's entry point: find a player.
 *
 * `web.player_directory` is the only view that is searched rather than filtered by a
 * known UUID, so it is the only place the SPA needs a query language at all. Two shapes
 * are supported and they cover everything an admin has to hand:
 *   - a UUID (someone pasted it out of a log) -> exact match;
 *   - anything else -> case-insensitive substring on `last_known_name`.
 *
 * A blank box lists the most recently seen players, because "who was on today" is the
 * question an admin usually opens this page with.
 *
 * NOTE `last_known_name` IS NULLABLE. A profile minted by an offline grant for a player
 * who has never joined has no name, so it can only ever be found by UUID - and it is
 * rendered with that fact visible rather than as an empty cell.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { PlayerDirectoryRow } from '@dimensionscore/shared';

import { usePolledView } from '../api/usePolledView';
import { useSession } from '../auth/SessionProvider';
import { SignInPanel } from '../components/SessionBar';

/** Long enough not to fire a query per keystroke, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** PostgREST treats `,` `.` `(` `)` as filter syntax and `*` as its LIKE wildcard. */
function escapeLikeTerm(term: string): string {
  return term.replace(/[,.()*]/g, ' ').trim();
}

const RECENT: Record<string, string> = { order: 'last_seen.desc.nullslast', limit: '25' };

/** Exported for the unit tests: this is the whole query language the SPA speaks. */
export function directoryQuery(term: string): Record<string, string> {
  const trimmed = term.trim();
  if (trimmed.length === 0) return RECENT;
  if (UUID_PATTERN.test(trimmed)) return { player_uuid: `eq.${trimmed.toLowerCase()}`, limit: '25' };
  const escaped = escapeLikeTerm(trimmed);
  if (escaped.length === 0) return RECENT;
  return { last_known_name: `ilike.*${escaped}*`, order: 'last_known_name.asc', limit: '25' };
}

export function InspectorPage(): React.JSX.Element {
  const { session, client } = useSession();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const query = useMemo(() => directoryQuery(debounced), [debounced]);
  const directory = usePolledView(client, 'player_directory', query, { enabled: session !== null });

  if (session === null) return <SignInPanel />;

  const rows = (directory.rows ?? []) as readonly PlayerDirectoryRow[];

  return (
    <section className="dc-card" data-testid="page-inspector">
      <h1>Player inspector</h1>

      <label className="dc-search">
        <span className="dc-dim">Search by name, or paste a UUID</span>
        <input
          type="search"
          data-testid="player-search"
          value={term}
          placeholder="Player name"
          autoComplete="off"
          onChange={(event) => setTerm(event.target.value)}
        />
      </label>

      {directory.status === 'error' ? (
        <p className="dc-error" role="status" data-testid="directory-error">
          {directory.error}
        </p>
      ) : rows.length === 0 && directory.status === 'ready' ? (
        <p className="dc-dim" data-testid="directory-empty">
          {debounced.trim().length === 0 ? 'No players on record yet.' : `No player matches ${debounced.trim()}.`}
        </p>
      ) : (
        <ul className="dc-results" data-testid="directory-results">
          {rows.map((row) => (
            <li key={row.player_uuid} data-testid="directory-row">
              <Link to={`/player/${row.player_uuid}`} data-testid="directory-link">
                <span className="dc-result-name">{row.last_known_name ?? <em className="dc-null">never joined</em>}</span>
                <code className="dc-result-uuid">{row.player_uuid}</code>
              </Link>
              <span className="dc-dim dc-result-seen">
                {row.last_seen === null ? 'never seen' : `last seen ${new Date(row.last_seen).toLocaleString()}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
